import type { AtlasErrorCode } from '@alphea-atlas/contracts';

/**
 * API client.
 *
 * Every request carries the session cookie and nothing else: the SPA never holds a
 * token, so there is nothing here to read out of storage. State-changing requests carry
 * the session's CSRF token as a header, which the server compares in constant time.
 */

export class ApiError extends Error {
  constructor(
    readonly code: AtlasErrorCode | 'network',
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export interface RequestOptions {
  method?: 'GET' | 'POST';
  body?: unknown;
  /** Cancels a superseded request so a stale response cannot overwrite a newer one. */
  signal?: AbortSignal;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  let response: Response;
  try {
    response = await fetch(path, {
      method,
      credentials: 'include',
      headers: {
        accept: 'application/json',
        ...(options.body === undefined ? {} : { 'content-type': 'application/json' }),
        ...(method === 'POST' && csrfToken ? { 'x-atlas-csrf': csrfToken } : {}),
      },
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError('network', 'Atlas is unreachable.', 0);
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as
      | { error?: { code?: AtlasErrorCode; message?: string } }
      | null;
    throw new ApiError(
      body?.error?.code ?? 'internal_error',
      body?.error?.message ?? 'The request failed.',
      response.status,
    );
  }
  return (await response.json()) as T;
}

// ------------------------------------------------------------------ response shapes

export interface SessionResponse {
  authenticated: boolean;
  /** True only for the registered owner; other authorized readers are read-only. */
  isOwner?: boolean;
  principal: { id: string; displayName: string } | null;
  actor: ActorSummary | null;
  authentication?: 'okta-session' | 'agent-token';
  csrfToken: string | null;
}

export interface ActorSummary {
  id: string;
  label: string;
  kind: string;
  lifecycle: 'pending' | 'active' | 'suspended' | 'revoked';
  credentialSubject: string;
  fingerprint: string;
  algorithm: string;
  enrolledAt: string;
  rotatedAt: string | null;
  revokedAt: string | null;
  retiredFingerprints: string[];
}

export interface TagSummary {
  id: string;
  namespace: string;
  key: string;
  displayName: string;
  order: number;
}

export interface ViewNodeDto {
  id: string;
  kind: 'program' | 'subproject' | 'section' | 'document' | 'unclassified';
  label: string;
  tagId?: string;
  documentId?: string;
  sourcePath?: string;
  documentCount: number;
  children: ViewNodeDto[];
}

export interface TreeResponse {
  generationId: string;
  filter: { description: string; groups: { namespace: string; tags: TagSummary[] }[] };
  nodes: ViewNodeDto[];
}

export interface DocumentSummary {
  documentId: string;
  title: string;
  sourcePath: string;
  repository: string;
  revision: string;
  contentDigest: string;
  updatedAt: string | null;
  tags: { tagId: string; origin: string; ruleId?: string }[];
  url: string;
}

export interface DocumentsResponse {
  generationId: string;
  filter: { description: string };
  total: number;
  documents: DocumentSummary[];
  nextCursor: string | null;
}

export interface EvidenceRefDto {
  sectionId: string;
  documentId: string;
  title: string;
  sourcePath: string;
  sectionAnchor?: string;
  canonicalUrl: string;
  immutableUrl?: string;
  snippet?: string;
  contentDigest?: string;
}

export interface KnowledgeNodeDto {
  id: string;
  kind: string;
  canonicalUrl: string;
  immutableUrl?: string;
  title: string;
  summary?: string;
  sourcePath?: string;
  sourceRevision?: string;
  curated: boolean;
}

export interface CitationSnapshotDto {
  knowledgeId: string;
  graphGeneration: string;
  algorithm: string;
  algorithmVersion: string;
  distinctCitingClaims: number;
  distinctCitingDocuments: number;
  distinctCitingRepositories: number;
  declaredCitations: number;
  extractedCitations: number;
  sameRepositoryCitations: number;
  influenceScore: number | null;
  computedAt: string;
}

export interface KnowledgeResultDto {
  node: KnowledgeNodeDto;
  score: number;
  claims: {
    id: string;
    statement: string;
    origin: string;
    evidence: EvidenceRefDto[];
  }[];
  relationships: { node: KnowledgeNodeDto; type: string; origin: string; evidenceCount: number }[];
  evidence: EvidenceRefDto[];
  citation: CitationSnapshotDto | null;
}

export interface DocumentMatchDto {
  sectionId: string;
  documentId: string;
  title: string;
  sourcePath: string;
  sectionAnchor?: string;
  canonicalUrl: string;
  immutableUrl?: string;
  snippet: string;
  frequency: number;
}

export interface SearchResponse {
  generationId: string;
  knowledge: KnowledgeResultDto[];
  documentMatches: DocumentMatchDto[];
  nextCursor: string | null;
}

export interface KnowledgeDetailResponse extends KnowledgeResultDto {
  generationId: string;
  backlinks: { node: KnowledgeNodeDto; type: string; origin: string; evidenceCount: number }[];
}

/** Present only for a caller that may actually propose a revision. */
export interface EditBase {
  content: string;
  contentDigest: string;
  documentRevisionId: string;
  revision: string;
}

export interface FileResponse {
  generationId: string;
  document: DocumentSummary & {
    isLatest: boolean;
    sections: { sectionId: string; anchor: string; heading: string; depth: number }[];
  };
  html: string;
  /** Null for read-only callers and for pinned historical revisions. */
  editBase: EditBase | null;
  links: { latest: string; immutable: string };
}

export interface GenerationResponse {
  generation: {
    generationId: string;
    revision: string;
    manifestDigest: string;
    repository: string;
    activatedAt: string;
  } | null;
  sync: {
    status: 'current' | 'updating' | 'stale' | 'unavailable';
    activeRevision: string | null;
    lastFailureCode: string | null;
    reconciliationIntervalSeconds: number;
  };
}

export interface AuthorityResponse {
  principal: { id: string; displayName: string; authentication: string };
  actors: ActorSummary[];
}

export interface ProposalDto {
  id: string;
  documentId: string;
  sourcePath: string;
  status: 'open' | 'conflict' | 'superseded' | 'promoted' | 'withdrawn';
  headRevisionId: string;
  supersededBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProposedRevisionDto {
  id: string;
  proposalId: string;
  content: string;
  contentDigest: string;
  tombstone: boolean;
  parents: string[];
  audit: {
    principalId: string;
    actorId: string;
    delegationId: string | null;
    taskRef: string | null;
    specRef: string | null;
    authentication: string;
    baseRevision: string;
    contentDigest: string;
    reason: string;
    timestamp: string;
  };
  createdAt: string;
}

export interface DiffHunkDto {
  kind: 'context' | 'added' | 'removed';
  line: string;
  baseLine: number | null;
  proposedLine: number | null;
}

export interface ProposalDiffResponse {
  proposal: ProposalDto;
  base: { revision: string; contentDigest: string };
  parents: string[];
  hunks: DiffHunkDto[];
}

export interface CreatedProposalResponse {
  proposal: ProposalDto;
  revision: ProposedRevisionDto;
  conflict: boolean;
  policy: {
    decision: 'allow' | 'allow-with-signals' | 'review-needed';
    signals: { code: string; severity: 'info' | 'warning'; message: string; evidence?: string }[];
  };
}

// ------------------------------------------------------------------------- requests

export const encodePath = (path: string): string =>
  path.split('/').map(encodeURIComponent).join('/');

export const atlas = {
  session: (signal?: AbortSignal) => api<SessionResponse>('/api/v1/session', { signal }),
  generation: () => api<GenerationResponse>('/api/v1/generations/current'),
  tags: () => api<{ tags: TagSummary[] }>('/api/v1/tags'),
  tree: (tags: string[]) =>
    api<TreeResponse>(`/api/v1/tree${tags.length ? `?tags=${encodeURIComponent(tags.join(','))}` : ''}`),
  documents: (tags: string[], sort: string) =>
    api<DocumentsResponse>(
      `/api/v1/documents?sort=${encodeURIComponent(sort)}${tags.length ? `&tags=${encodeURIComponent(tags.join(','))}` : ''}`,
    ),
  search: (query: string, signal: AbortSignal) =>
    api<SearchResponse>(`/api/v1/search?q=${encodeURIComponent(query)}&limit=8`, { signal }),
  knowledge: (id: string) => api<KnowledgeDetailResponse>(`/api/v1/knowledge/${encodeURIComponent(id)}`),
  file: (path: string, revision?: string) =>
    api<FileResponse>(`/api/v1/files/${encodePath(path)}${revision ? `?rev=${revision}` : ''}`),
  authority: () => api<AuthorityResponse>('/api/v1/authority/principal'),
  proposal: (id: string) =>
    api<{ proposal: ProposalDto; revisions: ProposedRevisionDto[]; reviewSignals: unknown[]; promotions: unknown[] }>(
      `/api/v1/proposals/${encodeURIComponent(id)}`,
    ),
  proposalDiff: (id: string) => api<ProposalDiffResponse>(`/api/v1/proposals/${encodeURIComponent(id)}/diff`),
  createProposal: (path: string, body: unknown) =>
    api<CreatedProposalResponse>(`/api/v1/files/${encodePath(path)}/proposals`, { method: 'POST', body }),
  promote: (id: string) =>
    api<{ receipt: { status: string; reason: string } }>(
      `/api/v1/proposals/${encodeURIComponent(id)}/promotions`,
      { method: 'POST', body: {} },
    ),
  logout: () => api<{ ok: boolean }>('/auth/logout', { method: 'POST', body: {} }),
};
